import styled from "@emotion/styled";
import { type ButtonProps, Button as RadixButton } from "@radix-ui/themes";
import type React from "react";

import { theme } from "@themes/themes";

interface ExtendedButtonProps extends ButtonProps {
  fullwidth?: boolean;
}

export const Button: React.FC<ExtendedButtonProps> = ({ children, fullwidth, ...props }) => {
  return (
    <StyledButton {...(fullwidth && { "data-fullwidth": true })} {...props} disabled={props.disabled}>
      {children}
    </StyledButton>
  );
};

const StyledButton = styled(RadixButton)`
  border-radius: 4px;
  border: 1px solid ${theme.color("accent-9")};
  background: ${theme.color("accent-6")};
  box-shadow: none;
  width: ${(props) => ((props as any)["data-fullwidth"] ? "100%" : "auto")};
  transition: all 0.2s ease;

  &:hover {
    cursor: pointer;
    background: linear-gradient(${theme.color("white-20")}, ${theme.color("white-20")}), ${theme.color("accent-6")};
  }

  &:active {
    transform: scale(0.98);
    background: ${theme.color("accent-7")};
  }

  &.rt-variant-outline {
    background: ${theme.color("gray-1")};
    color: ${theme.color("accent-contrast")};
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.24px;
    padding: 8px 16px;
    height: 32px;
  }

  &.rt-variant-soft {
    background: transparent;
    border: 1px solid transparent;
    color: ${theme.color("accent-contrast")};
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.24px;
    padding: 8px 16px;
    height: 32px;
  }

  &.rt-variant-soft:hover {
    background: ${theme.color("white-20")};
  }

  &.rt-variant-classic {
    background: transparent;
    border: 1px solid ${theme.color("gray-5")};
    font-size: 12px;
    transition: all 0.15s ease;
  }

  &.rt-BaseButton:where(.rt-variant-classic)::after {
    background-image: none;
    box-shadow: none;
  }
  &.rt-BaseButton:where(.rt-variant-classic):where(:hover)::after {
    background: ${theme.color("gray-5")};
    border-radius: 0;
  }
  &.rt-variant-surface {
    background: ${theme.color("gray-3")};
    border: 1px solid ${theme.color("gray-6")};
    color: ${theme.color("accent-contrast")};
    font-size: 12px;
  }

  &.rt-BaseButton:where(.rt-variant-surface)::after {
    background-image: none;
    box-shadow: none;
  }

  &.rt-BaseButton:where(.rt-variant-surface):where(:hover)::after {
    background: ${theme.color("gray-5")};
    border-radius: 0;
  }

  &.rt-variant-ghost {
    background: transparent;
    border: none;
    color: ${theme.color("accent-contrast")};
    font-size: 12px;
  }

  &.rt-variant-ghost:hover {
    background: none;
  }

  &:hover:disabled,
  &:hover[disabled],
  &:disabled,
  &[disabled] {
    background: transparent !important;
    color: ${theme.color("gray-8")};
    border-color: ${theme.color("gray-5")};
    cursor: not-allowed;
  }

  &.rt-variant-ghost:disabled,
  &.rt-variant-ghost[disabled],
  &.rt-variant-soft:disabled,
  &.rt-variant-soft[disabled] {
    border: none;
  }
`;
